import { useMemo } from 'react';

import { getMsUntilNextSecond } from '#shared';

import { usePollingQuery } from '../../usePollingQuery';
import { useGetNewCasesQuery } from '../newCases.api';

import type { NewCase } from '#entities';
import type { PollingQueryResult } from '../types';

const TARGET_SECOND = 5;

const getNewCasesDelay = () => getMsUntilNextSecond(TARGET_SECOND);

export const useQueryNewCases = (): PollingQueryResult<NewCase[]> => {
  const queryResult = useGetNewCasesQuery(undefined, {
    pollingInterval: 0,
  });

  const pollingResult = usePollingQuery(queryResult, {
    getDelay: getNewCasesDelay,
  });

  return useMemo(
    () => ({
      data: pollingResult.data?.data ?? [],
      generatedAt: pollingResult.data?.generatedAt ?? null,
      // TMDXNC-238: newCases is deliberately OUTSIDE the TSMED-1482
      // stale-while-empty net. That net assumes an empty list always means
      // "backend unavailable"; for newCases it does not — NOP routinely has
      // zero new cases, and holding the last non-empty payload kept closed
      // cases on the board indefinitely. An empty poll is a real state here, so
      // it passes straight through and the board can never be stale this way.
      isStale: false,
      validation: pollingResult.data?.validation ?? null,
      isLoading: pollingResult.isLoading,
      isFetching: pollingResult.isFetching,
      isError: pollingResult.isError,
      error: pollingResult.error,
      lastSentTime: pollingResult.lastSentTime,
      lastReceiveTime: pollingResult.lastReceiveTime,
    }),
    [pollingResult],
  );
};
